import React, { useCallback, useEffect, useRef, useState } from "react";

import { Grid, Typography, Box, IconButton, Stack } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseIcon from "@mui/icons-material/Close";

import {
    BreadCrumbs,
    Input,
    Select,
    ExitConfirmationModal,
    LoadingBar,
    InputMobileNumber,
    InputEmail,
    OnOffSwitch
} from "../Common";

import {
    trakrefFontFamily,
    trakrefFontFamilySemiBold,
    trakrefFontFamilyBold,
} from "../../utils";

import useFileUpload from "../../hooks/useFileUpload";
import useFormDirty from "../../hooks/useFormDirty";
import useNavigate from "../../hooks/useNavigate";
import useBreadCrumbs from "../../hooks/useBreadCrumbs";

import { trakrefBlue, Grey3 } from "../../color_utils";
import Routes from "../../routes";
import UserInfo from "../../UserInfo"
import toast from "react-hot-toast";
import {
    trakrefSettingsToggles,
    weightUnitOptions,
    findWeightUnitOption,
    CULTURE_DISPLAY,
    CULTURE_NOTE
} from "./utils";

const initialValues = {
    // General Information
    logo: null,
    instanceId: null,
    name: null,
    shortName: null,
    accountType: null,
    accountTypeId: null,
    industryTypeId: null,
    energyStarPartnerTypeId: null,
    leakRateCalculationTypeId: null,
    // Trakref Settings
    unknownCylindersAllowed: false,
    workOrderNumberRequired: false,
    techCertificationRequired: false,
    faultInformationRequired: false,
    systemTaggingRequired: false,
    trakrefTagsUsed: false,
    customerTagsUsed: false,
    v3: false,
    vitalStatsIfLeakRateLimitExceeded: false,
    restrictInstallEventToOwnerOperatorUsers: false,
    allowZeroWeightRecoveryForShutdownAndRetrofitEvents: false,
    hasAnalytics: false,
    isOptedIn: false,
    // Default User Settings
    weightUnits: "Lbs",
    culture: CULTURE_DISPLAY,
    // Billing Address
    street1: null,
    street2: null,
    city: null,
    postalCode: null,
    stateCode: null,
    countryCode: null,
    title: null,
    // Primary Contact
    firstName: null,
    lastName: null,
    mainPhone: null,
    mainPhoneCountryCode: null,
    mainPhoneExt: null,
    alternatePhone: null,
    alternatePhoneCountryCode: null,
    alternatePhoneExt: null,
    email: null,
    fax: null,
    faxCountryCode: null
}

const intialSelectValues = {
    energyStarPartnerTypes: [],
    industryTypes: [],
    leakRateCalculationTypes: [],
    states: [],
    countries: []
}

// Account Type options for Create — System Owner / Contractor Lite only
// (Trakref Pro, SF6, Reclaim excluded per #13529 AC #10a).
const accountTypeOptions = [
    { id: 1, name: "System Owner" },
    { id: 2, name: "Contractor Lite" },
];

const LEAK_RATE_DEFAULT_NAME = "EPA Method 1 Annualized - Since Last Service Event";

const isEmpty = (v) => v === null || v === undefined || v === "";

const formatValue = (val) => {
    if (val && val.length > 0 && val[0] === "+") {
        const i = val.indexOf(" ");
        return val.substring(val.length + 1, i).trimStart();
    }
    return val;
};


const AccountDetailsForm = ({ isCreate = false, match }) => {
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [showExitModal, setShowExitModal] = useState(false);
    const [accountDetails, setAccountDetaiils] = useState(initialValues);
    const [options, setOptions] = useState(intialSelectValues);
    const [logo, setLogo] = useState(null);
    const [states, setStates] = useState([]);
    const fileInputRef = useRef();

    const navigate = useNavigate({
        pageName:'AccountDetailsForm'
    });

    const breadCrumbs = useBreadCrumbs({
        path: { id: 'AccountDetailsForm', text: isCreate ? 'Add New Account' : 'Edit', link: window.location.pathname }
    });

    const { initializeData, isDirty } = useFormDirty();
    const { readFileDataAsBase64 } = useFileUpload();

    // When editing a specific account via /ManageAccounts/EditAccount/:id, use that id;
    // otherwise (self-service /EditAccountDetails) fall back to the current session account.
    const editAccountId = match?.params?.id || UserInfo.instanceId;

    // Create and edit-by-id are launched from Manage Accounts; self-service edit from Account Details.
    const isGccEdit = !isCreate && !!match?.params?.id;
    // Opt-in is editable only by system admins (the backend enforces the same rule in UpdateAccountDetails),
    // so gate on the user too — not just the route — in case route access ever broadens.
    const canEditOptIn = isGccEdit && UserInfo.isSystemAdministrator();
    const returnPath = (isCreate || !!match?.params?.id) ? "/ManageAccounts" : "/AccountDetails";

    const onHandleFormChange = (name, value) => {

        if (name === "countryCode") {
            const newStates = states.filter(x => x.countryCode === value);
            setOptions({
                ...options,
                states: newStates
            });
            setAccountDetaiils(prev => ({
                ...prev,
                stateCode: null
            }));
        }

        setAccountDetaiils(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const onValidateField = (value) => {
        return value && value.length > 0 ? false : true;
    };

    const onHandleFormSave = async () => {
        try {
            setIsSaving(true);

            // Required fields (both Create and Edit per #13529/#14312): Name, Account Type, Leak Rate, Weight Units.
            const requiredMissing = (
                isEmpty(accountDetails.accountTypeId) ||
                isEmpty(accountDetails.leakRateCalculationTypeId) ||
                isEmpty(accountDetails.weightUnits)
            );

            if (hasError || onValidateField(accountDetails.name) || requiredMissing) {
                setShowErrors(true);
                setIsSaving(false);
                toast.error("Please complete all required fields.", { duration: 6000 });
                return;
            }

            const body = {
                ...accountDetails,
                mainPhone: accountDetails.mainPhone && `${accountDetails.mainPhoneCountryCode} ${formatValue(accountDetails.mainPhone)}`,
                alternatePhone: accountDetails.alternatePhone && `${accountDetails.alternatePhoneCountryCode} ${formatValue(accountDetails.alternatePhone)}`,
                fax: accountDetails.fax && `${accountDetails.faxCountryCode} ${formatValue(accountDetails.fax)}`,
            }

            // Opted In is only sent by forms that actually show the toggle (create seeds it true).
            if (!isCreate && !canEditOptIn) {
                delete body.isOptedIn;
            }

            const url = isCreate
                ? Routes.Account.CreateAccount()
                : Routes.Account.UpdateAccountDetails(editAccountId);

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body)
            });

            if (response.status === 200) {
                if (isCreate) {
                    toast.success("Success. Account Created.", { duration: 6000 });
                    navigate.push("/ManageAccounts");
                } else {
                    toast.success("The account details have been successfully updated.", { duration: 6000 });
                    navigate.goBack(returnPath);
                }
            }
        }
        catch {
            toast.error("Something went wrong!", { duration: 6000 });
            setIsSaving(false);
        }
        finally {
            setIsSaving(false);
        }
    };

    const onHandleCloseForm = () => {
        if (isDirty(accountDetails)) {
            setShowExitModal(true);
            return;
        }

        navigate.goBack(returnPath);
    };

    const onUploadFileClick = () => {
        fileInputRef.current.click();
    };

    const onFileChange = async (e) => {
        const file = e.target.files[0];
        const byteFile = await readFileDataAsBase64(file);

        const body = {
            fileName: file.name,
            contentType: file.type,
            fileBytes: byteFile
        }

        const response = await fetch(Routes.File.UploadAccountLogo(), {
            method: "POST",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        });

        if (response.status === 200) {
            setLogo(URL.createObjectURL(file));
        }
    };

    const fetchAccountDetails = useCallback(async () => {
        try {
            setLoading(true);

            const response = await fetch(Routes.Account.GetAccountDetails(true, isCreate ? undefined : editAccountId));
            const data = await response.json();

            if (response.status !== 200) {
                return null;
            }

            const {
                energyStarPartnerTypes: energyStarPartnerTypesOption,
                industryTypes: industryTypesOption,
                leakRateCalculationTypes: leakRateCalculationTypesOption,
                states: stateOption,
                countries: countriesOption
            } = data;

            const energyStarPartnerTypes = energyStarPartnerTypesOption.map(({ id, displayName }) => ({ id, name: displayName }));
            const industryTypes = industryTypesOption.map(({ id, displayName }) => ({ id, name: displayName }));
            const leakRateCalculationTypes = leakRateCalculationTypesOption.map(({ id, longDisplayName }) => ({ id, name: longDisplayName }));
            const states = stateOption.map(({ stateCode, stateName, countryCode }) => ({ id: stateCode, name: stateName, countryCode }));
            const countries = countriesOption.map(({ countryCode, countryName }) => ({ id: countryCode, name: countryName }));

            let newAccountDetails;
            let filterdStates;

            if (isCreate) {
                // Create mode: ignore the current account's data; seed spec defaults (#13529).
                const defaultLeakRate =
                    leakRateCalculationTypes.find(x => x.name === LEAK_RATE_DEFAULT_NAME) ||
                    leakRateCalculationTypes[0];
                const defaultCountry = "US";
                filterdStates = states.filter(x => x.countryCode === defaultCountry);

                newAccountDetails = {
                    ...initialValues,
                    leakRateCalculationTypeId: defaultLeakRate ? defaultLeakRate.id : null,
                    // Trakref Settings — defaults per AC
                    unknownCylindersAllowed: true,
                    techCertificationRequired: true,
                    faultInformationRequired: true,
                    v3: true,
                    isOptedIn: true, // new accounts are opted in to V4 automatically (backend enforces this too)
                    // Default User Settings
                    weightUnits: "Lbs",
                    culture: CULTURE_DISPLAY,
                    // Billing Address
                    street1: "",
                    street2: "",
                    countryCode: defaultCountry,
                    // Primary Contact phone defaults
                    mainPhoneCountryCode: "+1",
                    alternatePhoneCountryCode: "+1",
                    faxCountryCode: "+1",
                };
            } else {
                filterdStates = states.filter(x => x.countryCode === data.countryCode);
                newAccountDetails = {
                    // General Information
                    instanceId: data.instanceId,
                    name: data.name,
                    shortName: data.shortName,
                    accountType: data.accountType,
                    accountTypeId: data.accountTypeId,
                    industryTypeId: data.industryTypeId,
                    energyStarPartnerTypeId: data.energyStarPartnerTypeId,
                    leakRateCalculationTypeId: data.leakRateCalculationTypeId,
                    // Trakref Settings
                    unknownCylindersAllowed: !!data.unknownCylindersAllowed,
                    workOrderNumberRequired: !!data.workOrderNumberRequired,
                    techCertificationRequired: !!data.techCertificationRequired,
                    faultInformationRequired: !!data.faultInformationRequired,
                    systemTaggingRequired: !!data.systemTaggingRequired,
                    trakrefTagsUsed: !!data.trakrefTagsUsed,
                    customerTagsUsed: !!data.customerTagsUsed,
                    v3: !!data.v3,
                    vitalStatsIfLeakRateLimitExceeded: !!data.vitalStatsIfLeakRateLimitExceeded,
                    restrictInstallEventToOwnerOperatorUsers: !!data.restrictInstallEventToOwnerOperatorUsers,
                    allowZeroWeightRecoveryForShutdownAndRetrofitEvents: !!data.allowZeroWeightRecoveryForShutdownAndRetrofitEvents,
                    hasAnalytics: !!data.hasAnalytics,
                    isOptedIn: !!data.isOptedIn,
                    // Default User Settings
                    weightUnits: data.weightUnitId ? (findWeightUnitOption(data.weightUnitId)?.id ?? data.weightUnitId) : "Lbs",
                    culture: CULTURE_DISPLAY,
                    // Billing Address
                    street1: data.street1 ? data.street1 : "",
                    street2: data.street2 ? data.street2 : "",
                    city: data.city,
                    postalCode: data.postalCode,
                    stateCode: data.stateCode,
                    countryCode: data.countryCode,
                    title: data.title,
                    // Primary Contact
                    firstName: data.firstName,
                    lastName: data.lastName,
                    mainPhone: data.mainPhone,
                    mainPhoneCountryCode: data.mainPhone && data.mainPhone[0] === "+" ? data.mainPhone.split(" ")[0].trimStart() : "+1",
                    mainPhoneExt: data.mainPhoneExt,
                    alternatePhone: data.alternatePhone,
                    alternatePhoneCountryCode: data.alternatePhone && data.alternatePhone[0] === "+" ? data.alternatePhone.split(" ")[0].trimStart() : "+1",
                    alternatePhoneExt: data.alternatePhoneExt,
                    email: data.email,
                    fax: data.fax,
                    faxCountryCode: data.fax && data.fax[0] === "+" ? data.fax.split(" ")[0].trimStart() : "+1"
                };
            }

            setLogo(isCreate ? null : data.logo);
            setStates(states);
            setOptions({
                energyStarPartnerTypes,
                industryTypes,
                leakRateCalculationTypes,
                states: filterdStates,
                countries
            });
            setAccountDetaiils(newAccountDetails);
            initializeData(newAccountDetails);
        }
        catch {
            setLoading(false);
        }
        finally {
            setLoading(false);
        }
    }, [editAccountId, isCreate]);

    useEffect(() => {
        fetchAccountDetails();
    }, [fetchAccountDetails]);

    if (loading) {
        return <LoadingBar />;
    }

    // Required-field errors surface only after a failed Save (showErrors), in both create and edit.
    const nameError = showErrors && onValidateField(accountDetails.name);

    // Opted In is a GCC-only control: shown to system admins editing via Manage Accounts, auto-true on
    // Create, and never shown on the self-service Account Details edit.
    const visibleToggleFields = canEditOptIn
        ? [...trakrefSettingsToggles, { name: "isOptedIn", label: "Opted In to V4", hint: "When off, users on this account see the V4 Disabled page at login" }]
        : trakrefSettingsToggles;

    // Create offers System Owner / Contractor Lite only. Edit keeps those two, but if the account's
    // existing type is a legacy one (SF6 / Reclaim / Trakref Pro) it is added so the value isn't lost (#14312).
    const accountTypeSelectOptions =
        !isCreate &&
        !isEmpty(accountDetails.accountTypeId) &&
        !accountTypeOptions.some(o => o.id === accountDetails.accountTypeId)
            ? [...accountTypeOptions, { id: accountDetails.accountTypeId, name: accountDetails.accountType }]
            : accountTypeOptions;

    return (
        <>
            <BreadCrumbs
                path={(isCreate || match?.params?.id) ? [
                    { text: "Geek Command Center", to: "/GeekCommandCenter" },
                    { text: "Manage Accounts", to: "/ManageAccounts" },
                    { text: isCreate ? "Add New Account" : "Edit Account" }
                ] : breadCrumbs.getSortedBreadCrumbs()}
            />

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexDirection: "row",
                    maxWidth: "67%",
                }}
            >
                <Box
                    style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        flexDirection: "row",
                    }}
                >
                    <IconButton aria-label="close" onClick={onHandleCloseForm}>
                        <CloseIcon />
                    </IconButton>

                    <Typography
                        variant="h5"
                        sx={{ "&.MuiTypography-root": { fontFamily: trakrefFontFamily } }}
                        style={{ color: "#1A173B" }}
                    >
                        {isCreate ? "Add New Account" : "Edit Account Details"}
                    </Typography>
                </Box>

                <LoadingButton
                    variant="contained"
                    loading={isSaving}
                    onClick={onHandleFormSave}
                    sx={{
                        "& .MuiCircularProgress-root": {
                            color: "#FFFFFF",
                            width: "21px !important",
                            height: "21px !important",
                        },
                        "&.MuiButton-root": {
                            fontFamily: trakrefFontFamilyBold,
                            backgroundColor: trakrefBlue,
                            letterSpacing: "1.2px",
                        },
                    }}
                >
                    <span style={{ color: isSaving ? "transparent" : "#fff" }}>
                        {isCreate ? "Create Account" : "Edit Account Details"}
                    </span>
                </LoadingButton>
            </Box>

            <Grid
                container
                ml={4}
                sx={{
                    maxWidth: "65%",
                    mt: "-1px",
                    mb: "5rem",
                    "& .MuiGrid-root": {
                        padding: "15px 0px 0px 3.5px",
                    },
                }}
                spacing={2}
            >
                <Grid item xs={12}>
                    <div className="editprofile-header-container">
                        {logo ? (
                            <img className="greenImageLarge" src={logo} />
                        ) : (
                            <div className="greenCircleLarge">{isCreate ? "+" : accountDetails.shortName}</div>
                        )}
                        <div>
                            <Typography
                                variant="h5"
                                sx={{
                                    "&.MuiTypography-root": {
                                        fontFamily: trakrefFontFamilySemiBold,
                                        fontSize: "16px",
                                    },
                                }}
                                style={{ color: "#333333" }}
                            >
                                {isCreate ? (accountDetails.name || "New Account") : accountDetails.name}
                            </Typography>
                            {/* Upload Logo is excluded from V4 Add New Account (#13529 AC #24) */}
                            {!isCreate &&
                                <>
                                    <span className="update-image-label" onClick={onUploadFileClick}>
                                        {logo ? "Update Logo" : "Upload Logo"}
                                    </span>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        style={{ display: "none " }}
                                        accept="image/png, image/gif, image/jpeg"
                                        onChange={onFileChange}
                                    />
                                </>
                            }
                        </div>
                    </div>

                    <Grid container spacing={6}>
                        {/* LEFT COLUMN — General Information, Trakref Settings, Default User Settings */}
                        <Grid item xs={12} md={6}>
                            <Stack spacing={4}>
                                <div>
                                    <p className="inputfields-header-label">
                                        General Information
                                    </p>
                                    <Stack spacing={3}>
                                        {/* Account Type is editable in both Create and Edit (#14312, PM 2026-04-22). */}
                                        <Select
                                            required
                                            label="Account Type"
                                            options={accountTypeSelectOptions}
                                            value={accountDetails.accountTypeId}
                                            error={showErrors && isEmpty(accountDetails.accountTypeId)}
                                            customHelperText="The Account Type field is required."
                                            onChange={(e) => onHandleFormChange("accountTypeId", e.target.value)}
                                        />
                                        <Input
                                            required
                                            label="Name"
                                            value={accountDetails.name}
                                            error={nameError}
                                            onChange={(e) => onHandleFormChange("name", e.target.value)}
                                            maxCharacterLength={75}
                                            alwaysShowHelper
                                            helperText={nameError ? "The Name field is required." : "Max 75 characters"}
                                        />
                                        <Select
                                            label="Industry Type"
                                            options={options.industryTypes}
                                            value={accountDetails.industryTypeId}
                                            onChange={(e) => onHandleFormChange("industryTypeId", e.target.value)}
                                        />
                                        <Select
                                            label="Energy Star Partner Type"
                                            options={options.energyStarPartnerTypes}
                                            value={accountDetails.energyStarPartnerTypeId}
                                            onChange={(e) => onHandleFormChange("energyStarPartnerTypeId", e.target.value)}
                                        />
                                        <Select
                                            required
                                            label="Leak Rate Calculation Type"
                                            options={options.leakRateCalculationTypes}
                                            value={accountDetails.leakRateCalculationTypeId}
                                            error={showErrors && isEmpty(accountDetails.leakRateCalculationTypeId)}
                                            customHelperText="The Leak Rate Calculation Type field is required."
                                            onChange={(e) => onHandleFormChange("leakRateCalculationTypeId", e.target.value)}
                                        />
                                    </Stack>
                                </div>

                                <div>
                                    <p className="inputfields-header-label">
                                        Trakref Settings
                                    </p>
                                    <Stack spacing={1}>
                                        {visibleToggleFields.map(({ name, label, hint, ownerOperatorAdminOnly, systemAdminOnly }) => (
                                            <div key={name}>
                                                <OnOffSwitch
                                                    id={name}
                                                    label={label}
                                                    value={accountDetails[name]}
                                                    disabled={
                                                        (ownerOperatorAdminOnly && !UserInfo.isNativeAdminRole())
                                                        || (systemAdminOnly && !UserInfo.isSystemAdministrator())
                                                    }
                                                    onChange={(e) => onHandleFormChange(name, e.target.checked)}
                                                />
                                                {hint &&
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ color: Grey3, fontFamily: trakrefFontFamily, display: "block", mt: "-4px" }}
                                                    >
                                                        {hint}
                                                    </Typography>
                                                }
                                            </div>
                                        ))}
                                    </Stack>
                                </div>

                                <div>
                                    <p className="inputfields-header-label">
                                        Default User Settings
                                    </p>
                                    <Stack spacing={3}>
                                        <Select
                                            required
                                            label="Weight Units"
                                            options={weightUnitOptions}
                                            value={accountDetails.weightUnits}
                                            showNullOption={false}
                                            error={showErrors && isEmpty(accountDetails.weightUnits)}
                                            customHelperText="The Weight Units field is required."
                                            onChange={(e) => onHandleFormChange("weightUnits", e.target.value)}
                                        />
                                        <Input
                                            label="Culture"
                                            value={accountDetails.culture}
                                            disabled
                                            alwaysShowHelper
                                            helperText={CULTURE_NOTE}
                                        />
                                    </Stack>
                                </div>
                            </Stack>
                        </Grid>

                        {/* RIGHT COLUMN — Billing Address, Primary Contact */}
                        <Grid item xs={12} md={6}>
                            <Stack spacing={4}>
                                <div>
                                    <p className="inputfields-header-label">
                                        Billing Address
                                    </p>
                                    <Stack spacing={3}>
                                        <Input
                                            label="Street 1"
                                            value={accountDetails.street1}
                                            onChange={(e) => onHandleFormChange("street1", e.target.value)}
                                        />
                                        <Input
                                            label="Street 2"
                                            value={accountDetails.street2}
                                            onChange={(e) => onHandleFormChange("street2", e.target.value)}
                                        />
                                        <Input
                                            label="City"
                                            value={accountDetails.city}
                                            onChange={(e) => onHandleFormChange("city", e.target.value)}
                                        />
                                        <Select
                                            label="Country"
                                            options={options.countries}
                                            value={accountDetails.countryCode}
                                            onChange={(e) => onHandleFormChange("countryCode", e.target.value)}
                                        />
                                        {
                                            options.states.length > 0 ?
                                                <Select
                                                    label="State"
                                                    options={options.states}
                                                    value={accountDetails.stateCode}
                                                    onChange={(e) => onHandleFormChange("stateCode", e.target.value)}
                                                /> :
                                                <Input
                                                    label="State"
                                                    value={accountDetails.stateCode}
                                                    onChange={(e) => onHandleFormChange("stateCode", e.target.value)}
                                                />
                                        }
                                        <Input
                                            label="Zip Code"
                                            value={accountDetails.postalCode}
                                            onChange={(e) => onHandleFormChange("postalCode", e.target.value)}
                                        />
                                    </Stack>
                                </div>

                                <div>
                                    <p className="inputfields-header-label">
                                        Primary Contact
                                    </p>
                                    <Stack spacing={3}>
                                        <Input
                                            label="Title"
                                            value={accountDetails.title}
                                            onChange={(e) => onHandleFormChange("title", e.target.value)}
                                        />
                                        <Input
                                            label="First name"
                                            value={accountDetails.firstName}
                                            onChange={(e) => onHandleFormChange("firstName", e.target.value)}
                                        />
                                        <Input
                                            label="Last name"
                                            value={accountDetails.lastName}
                                            onChange={(e) => onHandleFormChange("lastName", e.target.value)}
                                        />
                                        <InputEmail
                                            label="Email"
                                            value={accountDetails.email}
                                            isValid={(e) => setHasError(e)}
                                            onChange={(e) => onHandleFormChange("email", e.target.value)}
                                        />
                                        <InputMobileNumber
                                            fullWidth
                                            label="Mobile number"
                                            value={accountDetails.mainPhone}
                                            isValid={(e) => setHasError(e)}
                                            onChange={(e) => onHandleFormChange("mainPhone", e.target.value)}
                                            getCountryCode={(value) =>
                                                onHandleFormChange("mainPhoneCountryCode", value)
                                            }
                                        />
                                        <Input
                                            label="Mobile number extension"
                                            value={accountDetails.mainPhoneExt}
                                            onChange={(e) => onHandleFormChange("mainPhoneExt", e.target.value)}
                                        />
                                        <InputMobileNumber
                                            fullWidth
                                            label="Alternate mobile number"
                                            value={accountDetails.alternatePhone}
                                            isValid={(e) => setHasError(e)}
                                            onChange={(e) => onHandleFormChange("alternatePhone", e.target.value)}
                                            getCountryCode={(value) =>
                                                onHandleFormChange("alternatePhoneCountryCode", value)
                                            }
                                        />
                                        <Input
                                            label="Alternate mobile number extension"
                                            value={accountDetails.alternatePhoneExt}
                                            onChange={(e) => onHandleFormChange("alternatePhoneExt", e.target.value)}
                                        />
                                        <InputMobileNumber
                                            fullWidth
                                            label="Fax"
                                            value={accountDetails.fax}
                                            isValid={(e) => setHasError(e)}
                                            onChange={(e) => onHandleFormChange("fax", e.target.value)}
                                            getCountryCode={(value) =>
                                                onHandleFormChange("faxCountryCode", value)
                                            }
                                        />
                                    </Stack>
                                </div>
                            </Stack>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <ExitConfirmationModal
                open={showExitModal}
                close={() => setShowExitModal(false)}
                leaveOnClick={() => navigate.goBack(returnPath)}
                stayOnClick={() => setShowExitModal(false)}
                isEditing={!isCreate && !!accountDetails.accountType}
            />
        </>
    )
}

export default AccountDetailsForm;
